import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import type { Retailer } from "@/pages/admin/retailers/types";

const REASONS = [
  "Requested by Retailer",
  "Non-Compliance",
  "Suspicious Activity",
  "Inactive Account",
  "Other",
];

interface DeactivateModalProps {
  open: boolean;
  onClose: () => void;
  retailer: Retailer | null;
  onConfirm: (id: string, reason: string, remarks: string) => Promise<void>;
}

export default function DeactivateModal({ open, onClose, retailer, onConfirm }: DeactivateModalProps) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setRemarks("");
      setErrors({});
    }
  }, [open]);

  async function handleDeactivate() {
    if (!reason) {
      setErrors({ reason: "Please select a reason" });
      return;
    }
    if (!retailer) return;
    setSaving(true);
    try {
      await onConfirm(retailer.id, reason, remarks);
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to deactivate retailer");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Deactivate this retailer?"
      confirmVariant="danger"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Keep Active
          </Button>
          <Button variant="danger" onClick={handleDeactivate} loading={saving}>
            Deactivate
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3 rounded-lg border border-[#FDE68A] bg-warning-bg p-3.5">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
        <p className="text-sm text-amber-900">
          The retailer will be logged out immediately and will not be able to create new mandates. Existing
          active mandates will continue to run normally.
        </p>
      </div>

      <div className="mt-5 space-y-4">
        <Select
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          options={REASONS.map((r) => ({ value: r, label: r }))}
          placeholder="Select a reason"
          error={errors.reason}
        />
        <Textarea
          label="Remarks"
          rows={3}
          maxLength={500}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Optional additional context (max 500 characters)"
        />
      </div>
    </Modal>
  );
}